import React from 'react'
import { parse } from 'query-string'

// Components
import { DetailView } from './DetailView'
import { RouteComponentProps } from 'react-router-dom'
import { CanvasRendererProvider } from '@sketch-hq/sketch-web-renderer'
import { AnnotationOverlayContext } from 'modules/annotations/components'
import { AnnotationQueryVariablesProvider } from 'modules/annotations/context'
import { Pagination } from '../types'

// Types
import {
  useGetDetailViewArtboardQuery,
  GetDetailViewArtboardQuery,
  VersionFragment,
  ArtboardDetailInfoFragment,
} from '@sketch/gql-types'
import {
  RevisionChangedProps,
  UseRevisionSubscriptionsProps,
} from 'modules/activity/operations'

// Hooks
import {
  useQueryRetry,
  QueryParams,
  RouteParams,
  DocumentSidebarLayoutExtraProps,
} from '@sketch/modules-common'
import {
  useVersioning,
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
} from 'modules/versioning'
import { useOnCurrentRevisionChange } from './useOnCurrentRevisionChange'
import { RevisionSubscriptions } from '../components/RevisionSubscriptions'
import { removeAnnotationFromQueryParams } from 'modules/annotations/utils'
import { useGetActiveSidebarTab } from '../components/ShareSidebarTabContext'
import { useStableHandler } from '@sketch/utils'

const getArtboard = (result: { data?: GetDetailViewArtboardQuery }) => {
  const artboard = result?.data?.artboard
  // at some point Apollo (while loading) returns an artboard which contains only
  // a symbol of id, all other properties are non existing
  // this gives a false impression, that the object is there, but it isn't
  return artboard?.identifier ? artboard : undefined
}

type MaybeArtboard = ReturnType<typeof getArtboard>

interface UseRetryGetDetailViewArtboardQueryProps {
  permanentArtboardShortId: string
  versionShortId?: string
  shareIdentifier: string
  enabled: boolean
}

const useRetryGetDetailViewArtboardQuery = ({
  permanentArtboardShortId,
  versionShortId,
  shareIdentifier,
  enabled = true,
}: UseRetryGetDetailViewArtboardQueryProps) =>
  useQueryRetry(
    useGetDetailViewArtboardQuery({
      shouldInvalidatePrevious: (prev, curr) =>
        prev?.permanentArtboardShortId !== curr?.permanentArtboardShortId,
      // To account for artboard updates we're setting a cache-and-network
      // policy for artboard queries without an explicit version.
      fetchPolicy: versionShortId ? 'cache-first' : 'cache-and-network',
      variables: {
        permanentArtboardShortId: permanentArtboardShortId,
        documentVersionShortId: versionShortId,
        shareIdentifier,
      },
      skip: !enabled,
    }),
    {
      retryIf: result => {
        if (!enabled) return false
        const artboard = getArtboard(result)
        const files = (artboard && artboard.files) || []
        return files.some(file => !file?.url)
      },
    }
  )

const getPagination = (
  version: VersionFragment | undefined,
  artboard: ArtboardDetailInfoFragment | undefined
): Pagination | undefined => {
  if (!version || !artboard) return undefined
  const total = version.document?.artboardCount
  const current =
    artboard.documentOrder == null ? undefined : artboard.documentOrder

  return { current, total }
}

const getArtboardForSubscription = (
  maybeArtboard: MaybeArtboard
): UseRevisionSubscriptionsProps['artboard'] | null => {
  if (!maybeArtboard?.identifier || !maybeArtboard?.permanentArtboardShortId)
    return null

  return {
    identifier: maybeArtboard.identifier,
    permanentArtboardShortId: maybeArtboard.permanentArtboardShortId,
    uuid: maybeArtboard.uuid,
  }
}

export type DetailsViewRouteProps = RouteComponentProps<
  Partial<RouteParams<'ARTBOARD_REVISION'>> & RouteParams<'ARTBOARD_DETAIL'>
>
interface DetailViewQueryProps extends DetailsViewRouteProps {
  layoutProps: DocumentSidebarLayoutExtraProps
}

export const DetailViewQuery = ({
  match,
  history,
  location,
  layoutProps,
}: DetailViewQueryProps) => {
  const { permanentArtboardShortId, revisionDocShortId } = match.params

  const queryParams = parse(
    location.search || ''
  ) as QueryParams<'ARTBOARD_DETAIL'>

  const handleOnDraftAnnotation = useStableHandler(() => {
    if (queryParams.annotation) {
      history.replace({ search: removeAnnotationFromQueryParams(queryParams) })
    }
  })

  const versionContext = useVersioning()
  const activeSidebarTab = useGetActiveSidebarTab()

  const { onCurrentRevisionChange } = useOnCurrentRevisionChange()
  const { versionShortId, share, isViewingLatestVersion } = versionContext

  const artboardAtDocResult = useRetryGetDetailViewArtboardQuery({
    permanentArtboardShortId,
    versionShortId,
    shareIdentifier: share.identifier,
    enabled: true,
  })

  const artboardAtRevResult = useRetryGetDetailViewArtboardQuery({
    permanentArtboardShortId,
    versionShortId: revisionDocShortId || versionShortId,
    shareIdentifier: share.identifier,
    enabled: true,
  })

  const artboardAtLatestResult = useRetryGetDetailViewArtboardQuery({
    permanentArtboardShortId,
    /*
     * passing latestVersionId does not work
     * as on the latest version of the document the artboard might be deleted
     * leaving this comment here, so that if anyone would
     * try to optimise it in the future, they would be aware
     * more: https://github.com/sketch-hq/cloud-frontend/pull/800#discussion_r289356950
     */
    // versionId={latestVersionId}
    shareIdentifier: share.identifier,
    enabled: true,
  })

  const error =
    artboardAtDocResult.error ||
    artboardAtRevResult.error ||
    artboardAtLatestResult.error

  const loading =
    artboardAtDocResult.loading ||
    artboardAtRevResult.loading ||
    artboardAtLatestResult.loading

  if (isUpgradeToLatestNeeded(error)) {
    artboardAtLatestResult.refetch()
    return <UpgradeToLatestVersion error={error} />
  }

  const artboards = {
    docVersion: getArtboard(artboardAtDocResult),
    revVersion: getArtboard(artboardAtRevResult),
    latest: getArtboard(artboardAtLatestResult),
  }

  // if there is no latest revision or artboard passed,
  // we are assuming that we are looking at the latest version
  const isViewingLatestRevision =
    !artboards.latest ||
    !artboards.revVersion ||
    artboards.latest.revisionIdentifier ===
      artboards.revVersion.revisionIdentifier

  const { currentVersion } = (!versionContext.loading && versionContext) || {}

  const pagination = getPagination(currentVersion, artboards.docVersion)
  const artboardIdAtLatestVersion =
    artboards.latest?.documentVersionShortId || undefined

  const onRevisionChange = (data: RevisionChangedProps) => {
    /**
     * In all cases when revision changes (moved, deleted, created)
     * it is possible to find a case that latest revision also
     * has changed. Therefore it is a good idea, always refetch
     * the latest revision.
     *
     * Also see https://github.com/sketch-hq/Cloud/issues/1392
     */
    artboardAtLatestResult.refetch()
    if (data.selectedRevisionChange) {
      onCurrentRevisionChange(data)
    }
  }

  const subscriptionArtboard = getArtboardForSubscription(artboards.revVersion)

  const disableAnnotations =
    !artboards.docVersion ||
    !share.commentsEnabled ||
    activeSidebarTab === 'Inspect'

  return (
    <CanvasRendererProvider>
      <AnnotationQueryVariablesProvider
        shareIdentifier={share.identifier}
        permanentPageId={artboards?.revVersion?.page?.uuid || ''}
        permanentArtboardId={artboards?.revVersion?.uuid}
      >
        <AnnotationOverlayContext
          disabled={disableAnnotations}
          onDraftAnnotation={handleOnDraftAnnotation}
          isViewingLatestVersion={
            isViewingLatestRevision && isViewingLatestVersion
          }
        >
          <DetailView
            share={share}
            currentVersion={currentVersion}
            artboard={artboards.revVersion}
            isViewingLatestVersion={isViewingLatestRevision}
            isPrototypePlayEnabled={isViewingLatestRevision}
            artboardIdAtLatestVersion={artboardIdAtLatestVersion}
            pagination={pagination}
            error={error}
            loading={loading}
            layoutProps={layoutProps}
          />
          {subscriptionArtboard && (
            <RevisionSubscriptions
              artboard={subscriptionArtboard}
              onRevisionChange={onRevisionChange}
              share={share}
            />
          )}
        </AnnotationOverlayContext>
      </AnnotationQueryVariablesProvider>
    </CanvasRendererProvider>
  )
}
